import { useEffect, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { AlertTriangle, Clock, Download, FileSignature, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { WeekGrid, emptyWeek, weekHours } from "./WeekScheduleGrid";

const fmtIt = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtH = (n) => (n == null || n === "" ? "—" : `${String(n).replace(".", ",")}h`);
const nextDay = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const monthsBetween = (a, b) => {
    if (!a || !b) return null;
    const d = (new Date(b) - new Date(a)) / 86400000 + 1;
    return Math.round((d / 30.44) * 10) / 10;
};

export const LETTER_LABELS = {
    proroga: { title: "Comunicazione di proroga contratto T.D.", short: "proroga", button: "Genera comunicazione proroga", done: "Comunicazione inviata", law: "art. 21 D.Lgs. 81/2015" },
    trasformazione: { title: "Comunicazione di trasformazione a tempo indeterminato", short: "trasformazione", button: "Genera comunicazione trasformazione", done: "Comunicazione inviata", law: "art. 1 D.Lgs. 81/2015" },
};

const EMPTY_FORM = { data_lettera: "", scadenza_precedente: "", nuova_scadenza: "", numero_proroga: 1, causale: "", data_trasformazione: "", note: "" };
const EMPTY_ORARIO = { attiva: false, ore_nuove: "", ore_precedenti: "", settimane: "1", sett1: emptyWeek(), sett2: emptyWeek(), decorrenza: "", decorrenza_touched: false };

/** Sezione "Orario di lavoro": variazione ore rilevata dall'UNILAV + griglia settimanale come nel contratto. */
function OrarioSection({ info, orario, setOrario, totale }) {
    const upd = (patch) => setOrario((o) => ({ ...o, ...patch }));
    const updateWeek = (key) => (day, field, value) => setOrario((o) => ({ ...o, [key]: { ...o[key], [day]: { ...o[key][day], [field]: value } } }));
    const label = info?.variazione === "aumento" ? "Aumento" : info?.variazione === "diminuzione" ? "Diminuzione" : null;
    const diff = Number(orario.ore_nuove) > 0 && totale != null && Math.abs(totale - Number(orario.ore_nuove)) > 0.01;
    return (
        <div className={`rounded-md border px-3 py-2 ${orario.attiva ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-slate-50"}`} data-testid="letter-orario-section">
            <div className="flex items-start gap-2">
                <Clock className={`mt-0.5 h-4 w-4 flex-shrink-0 ${orario.attiva ? "text-amber-700" : "text-slate-400"}`} />
                <div className="flex-1 text-xs">
                    {label ? (
                        <div data-testid="letter-orario-variazione">
                            <p className="font-semibold text-amber-800">
                                {label} dell'orario contrattuale: l'UNILAV indica {fmtH(info.ore_nuove)} settimanali contro {fmtH(info.ore_precedenti)} del contratto precedente{info.fonte_precedenti ? ` (${info.fonte_precedenti})` : ""}.
                            </p>
                            <label className="mt-1 flex cursor-pointer items-center gap-2 text-amber-900">
                                <input type="checkbox" checked={orario.attiva} onChange={(e) => upd({ attiva: e.target.checked })} className="h-3.5 w-3.5 accent-[#376DB3]" data-testid="letter-orario-toggle" />
                                Riporta il nuovo orario nella comunicazione con la tabella settimanale
                            </label>
                        </div>
                    ) : info?.ore_nuove && info?.ore_precedenti ? (
                        <p className="text-slate-600" data-testid="letter-orario-invariato">Orario invariato: {fmtH(info.ore_nuove)} settimanali, come nel contratto precedente{info.fonte_precedenti ? ` (${info.fonte_precedenti})` : ""}.</p>
                    ) : (
                        <label className="flex cursor-pointer items-center gap-2 text-slate-700">
                            <input type="checkbox" checked={orario.attiva} onChange={(e) => upd({ attiva: e.target.checked })} className="h-3.5 w-3.5 accent-[#376DB3]" data-testid="letter-orario-toggle" />
                            Le ore settimanali non risultano dall'UNILAV: l'orario contrattuale cambia con questa comunicazione?
                        </label>
                    )}
                </div>
            </div>
            {orario.attiva && (
                <div className="mt-3 space-y-3" data-testid="letter-orario-form">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs">Ore settimanali precedenti</Label>
                            <Input type="number" min={0} step="0.5" value={orario.ore_precedenti} onChange={(e) => upd({ ore_precedenti: e.target.value })} className="mt-1 h-9 bg-white" data-testid="letter-ore-prec-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Nuove ore settimanali *</Label>
                            <Input type="number" min={0.5} step="0.5" value={orario.ore_nuove} onChange={(e) => upd({ ore_nuove: e.target.value })} className="mt-1 h-9 bg-white" data-testid="letter-ore-nuove-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Nuovo orario dal</Label>
                            <Input type="date" value={orario.decorrenza} onChange={(e) => upd({ decorrenza: e.target.value, decorrenza_touched: true })} className="mt-1 h-9 bg-white" data-testid="letter-orario-decorrenza-input" />
                        </div>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Orario di lavoro (come nel contratto)</p>
                            <div className="flex gap-1">
                                {["1", "2"].map((s) => (
                                    <button key={s} type="button" onClick={() => upd({ settimane: s })}
                                        className={`rounded-md border px-2.5 py-1 text-xs transition ${orario.settimane === s ? "border-[#376DB3] bg-[#376DB3]/5 font-semibold text-[#376DB3]" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                                        data-testid={`letter-settimane-${s}`}>
                                        {s === "1" ? "1 settimana" : "2 settimane alternate"}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <WeekGrid title={orario.settimane === "2" ? "1ª settimana" : "Settimana (uguale per tutte)"} week={orario.sett1} onChange={updateWeek("sett1")} testPrefix="letter-orari-s1" />
                            {orario.settimane === "2" && <WeekGrid title="2ª settimana" week={orario.sett2} onChange={updateWeek("sett2")} testPrefix="letter-orari-s2" />}
                        </div>
                        <p className={`mt-1.5 text-[11px] ${diff ? "font-semibold text-amber-700" : "text-slate-500"}`} data-testid="letter-orario-totale">
                            Totale griglia: {totale == null ? "—" : `${totale.toFixed(2).replace(".", ",").replace(/,?0+$/, "")}h`}{orario.settimane === "2" ? " (media delle 2 settimane)" : ""}
                            {diff ? ` ≠ ${fmtH(orario.ore_nuove)} dichiarate: verifica gli orari.` : ""} I giorni senza orario compariranno come «-» (riposo).
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export const ProrogaLetterDialog = ({ open, onOpenChange, employeeId, recordId, onDone, kind = "proroga" }) => {
    const L = LETTER_LABELS[kind] || LETTER_LABELS.proroga;
    const isProroga = kind === "proroga";
    const [loading, setLoading] = useState(false);
    const [ctx, setCtx] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(null); // "preview" | "send"
    const [selRecord, setSelRecord] = useState(null); // UNILAV chosen by the office (null = automatic/latest)
    const [orario, setOrario] = useState(EMPTY_ORARIO);

    useEffect(() => {
        if (open) setSelRecord(recordId || null);
        else setCtx(null);
    }, [open, recordId]);

    useEffect(() => {
        if (!open || !employeeId) return;
        setLoading(true);
        api.get("/contract-templates/proroga-letter-context", { params: { employee_id: employeeId, record_id: selRecord || undefined, kind } })
            .then(({ data }) => {
                setCtx(data);
                setForm({
                    ...EMPTY_FORM,
                    data_lettera: data.data_lettera || "",
                    scadenza_precedente: data.scadenza_precedente || "",
                    nuova_scadenza: data.nuova_scadenza || "",
                    numero_proroga: data.numero_proroga || 1,
                    data_trasformazione: data.data_trasformazione || "",
                });
                const o = data.orario || {};
                setOrario({ ...EMPTY_ORARIO, sett1: emptyWeek(), sett2: emptyWeek(), attiva: !!o.variazione,
                    ore_nuove: o.ore_nuove ?? "", ore_precedenti: o.ore_precedenti ?? "", decorrenza: data.decorrenza_orario || "" });
            })
            .catch((e) => toast.error(e.response?.data?.detail || `Impossibile caricare i dati della ${L.short}`))
            .finally(() => setLoading(false));
    }, [open, employeeId, selRecord, kind]); // eslint-disable-line react-hooks/exhaustive-deps

    // Il nuovo orario decorre dall'inizio della proroga / dalla trasformazione, finché l'ufficio non lo cambia a mano
    const autoDecorrenza = isProroga ? nextDay(form.scadenza_precedente) : form.data_trasformazione;
    useEffect(() => {
        if (!orario.decorrenza_touched && autoDecorrenza) setOrario((o) => (o.decorrenza === autoDecorrenza ? o : { ...o, decorrenza: autoDecorrenza }));
    }, [autoDecorrenza, orario.decorrenza_touched]);

    const totaleOrario = orario.attiva
        ? (orario.settimane === "2" ? (weekHours(orario.sett1) + weekHours(orario.sett2)) / 2 : weekHours(orario.sett1))
        : null;

    const activeRecordId = selRecord || ctx?.record_id || null;
    const records = ctx?.records || [];
    const recordLabel = (r) => {
        const parts = [];
        if (isProroga) {
            if (r.data_fine) parts.push(`Proroga fino al ${fmtIt(r.data_fine)}`);
            if (r.prev_contratto_scadenza) parts.push(`da scadenza ${fmtIt(r.prev_contratto_scadenza)}`);
        } else if (r.data_trasformazione) parts.push(`Trasformazione dal ${fmtIt(r.data_trasformazione)}`);
        if (r.data_invio) parts.push(`UNILAV del ${fmtIt(r.data_invio)}`);
        if (r.ore_settimanali) parts.push(`${String(r.ore_settimanali).replace(".", ",")}h/sett.`);
        if (r.codice_comunicazione) parts.push(`cod. ${r.codice_comunicazione}`);
        if (r.letter_doc_id) parts.push("✓ lettera già generata");
        return parts.join(" · ") || `UNILAV ${fmtIt(r.created_at)}`;
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const durata = isProroga ? monthsBetween(ctx?.data_assunzione, form.nuova_scadenza) : null;
    const causaleObbligatoria = isProroga && durata != null && durata > 12;
    const warnings = [];
    if (isProroga) {
        if (durata != null && durata > 24) warnings.push(`Durata complessiva ${durata} mesi: supera il limite ordinario di 24 mesi (art. 19 D.Lgs. 81/2015), salvo deroghe del CCNL.`);
        if (Number(form.numero_proroga) > 4) warnings.push("Oltre la 4ª proroga: superato il numero massimo di proroghe consentite (art. 21 D.Lgs. 81/2015).");
        if (form.scadenza_precedente && form.data_lettera && form.data_lettera > form.scadenza_precedente) warnings.push("La lettera è datata dopo la vecchia scadenza: la proroga va concordata prima della scadenza.");
    } else {
        if (form.data_trasformazione && form.data_lettera && form.data_lettera > form.data_trasformazione) warnings.push("La lettera è datata dopo la decorrenza della trasformazione.");
        if (form.scadenza_precedente && form.data_trasformazione) {
            const gapDays = (new Date(form.data_trasformazione) - new Date(form.scadenza_precedente)) / 86400000;
            if (gapDays > 1) warnings.push("La trasformazione decorre oltre un giorno dopo la scadenza del contratto a termine: verifica che il rapporto non si sia interrotto.");
        }
    }
    const orarioIncompleto = orario.attiva && (!(Number(orario.ore_nuove) > 0) || !(totaleOrario > 0));
    const invalid = !form.data_lettera || orarioIncompleto || (isProroga
        ? (!form.scadenza_precedente || !form.nuova_scadenza || form.nuova_scadenza <= form.scadenza_precedente || (causaleObbligatoria && !form.causale.trim()))
        : !form.data_trasformazione);

    const generate = async (save) => {
        setBusy(save ? "send" : "preview");
        try {
            const orari = { sett1: orario.sett1 };
            if (orario.settimane === "2") orari.sett2 = orario.sett2;
            const res = await api.post(
                "/contract-templates/proroga-letter",
                {
                    kind, employee_id: employeeId, record_id: activeRecordId, ...form, numero_proroga: Number(form.numero_proroga),
                    variazione_orario: orario.attiva,
                    ore_settimanali_nuove: orario.attiva && orario.ore_nuove !== "" ? Number(orario.ore_nuove) : null,
                    ore_settimanali_precedenti: orario.attiva && orario.ore_precedenti !== "" ? Number(orario.ore_precedenti) : null,
                    orari: orario.attiva ? orari : {}, settimane: Number(orario.settimane), decorrenza_orario: orario.attiva ? orario.decorrenza || null : null,
                    format: "pdf", save_to_documents: save,
                },
                { responseType: "blob" }
            );
            downloadBlob(res.data, `Comunicazione_${kind}_${ctx?.employee?.cognome || ""}_${ctx?.employee?.nome || ""}.pdf`);
            if (save) {
                toast.success("Comunicazione generata, archiviata e inviata al portale del dipendente per la firma");
                onDone && onDone();
                onOpenChange(false);
            } else {
                toast.success("Anteprima PDF scaricata (non archiviata)");
            }
        } catch (e) {
            let msg = "Errore nella generazione della comunicazione";
            try {
                const text = await e.response?.data?.text?.();
                if (text) msg = JSON.parse(text).detail || msg;
            } catch { /* keep default */ }
            toast.error(msg);
        } finally {
            setBusy(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={`max-h-[92vh] overflow-y-auto ${orario.attiva ? "sm:max-w-2xl" : "sm:max-w-xl"}`} data-testid="proroga-letter-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-[#376DB3]">
                        <FileSignature className="h-5 w-5" />
                        {L.title}
                    </DialogTitle>
                    <DialogDescription>
                        {ctx?.employee ? `${ctx.employee.cognome} ${ctx.employee.nome} · matr. ${ctx.employee.matricola || "—"}` : `Lettera al dipendente ai sensi dell'${L.law}`}
                        {ctx?.data_assunzione ? ` · assunzione ${fmtIt(ctx.data_assunzione)}` : ""}
                    </DialogDescription>
                </DialogHeader>

                {loading || !ctx ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : (
                    <div className="space-y-3">
                        {!ctx.template_available && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="proroga-no-template">
                                Modello "Comunicazione {L.short}" mancante: caricalo dalla pagina Modelli.
                            </div>
                        )}
                        {records.length > 0 && (
                            <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2" data-testid="letter-unilav-source-block">
                                <Label className="text-xs text-sky-900">
                                    {records.length > 1 ? `Preleva i dati dall'UNILAV (${records.length} comunicazioni di ${L.short})` : `Dati prelevati dall'UNILAV di ${L.short}`}
                                </Label>
                                {records.length > 1 ? (
                                    <Select value={activeRecordId || ""} onValueChange={(id) => setSelRecord(id)}>
                                        <SelectTrigger className="mt-1 h-8 bg-white text-xs" data-testid="letter-unilav-source-select">
                                            <SelectValue placeholder="Scegli la comunicazione UNILAV" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {records.map((r) => (
                                                <SelectItem key={r.id} value={r.id} data-testid={`letter-unilav-source-option-${r.id}`}>
                                                    {recordLabel(r)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <p className="mt-0.5 text-xs text-sky-800" data-testid="letter-unilav-source-single">{recordLabel(records[0])}</p>
                                )}
                            </div>
                        )}
                        {ctx.letter_doc_id && (
                            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="proroga-already">
                                Per questa {L.short} è già stata generata una comunicazione: generandone un'altra verrà archiviata come nuovo documento.
                            </div>
                        )}
                        {isProroga ? (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Scadenza precedente</Label>
                                        <Input type="date" value={form.scadenza_precedente} onChange={set("scadenza_precedente")} className="mt-1 h-9" data-testid="proroga-prev-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Nuova scadenza</Label>
                                        <Input type="date" value={form.nuova_scadenza} onChange={set("nuova_scadenza")} className="mt-1 h-9" data-testid="proroga-new-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Data della lettera</Label>
                                        <Input type="date" value={form.data_lettera} onChange={set("data_lettera")} className="mt-1 h-9" data-testid="proroga-date-input" />
                                        <p className="mt-0.5 text-[10px] text-slate-400">Proposta: giorno lavorativo precedente la vecchia scadenza</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Numero proroga</Label>
                                        <Input type="number" min={1} max={10} value={form.numero_proroga} onChange={set("numero_proroga")} className="mt-1 h-9" data-testid="proroga-number-input" />
                                        <p className="mt-0.5 text-[10px] text-slate-400">
                                            {ctx.proroghe_precedenti?.length ? `Precedenti: ${ctx.proroghe_precedenti.map(fmtIt).join(", ")}` : "Nessuna proroga precedente registrata"}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        Causale / motivazione {causaleObbligatoria ? <span className="font-semibold text-red-600">(obbligatoria: durata complessiva {durata} mesi &gt; 12)</span> : <span className="text-slate-400">(facoltativa{durata != null ? ` · durata complessiva ${durata} mesi` : ""})</span>}
                                    </Label>
                                    <Textarea value={form.causale} onChange={set("causale")} rows={2} className="mt-1 text-sm" placeholder="Es. esigenze temporanee e oggettive legate all'incremento delle commesse presso il cantiere…" data-testid="proroga-causale-input" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Data di trasformazione (decorrenza)</Label>
                                        <Input type="date" value={form.data_trasformazione} onChange={set("data_trasformazione")} className="mt-1 h-9" data-testid="trasf-date-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Scadenza del contratto a termine <span className="text-slate-400">(facoltativa)</span></Label>
                                        <Input type="date" value={form.scadenza_precedente} onChange={set("scadenza_precedente")} className="mt-1 h-9" data-testid="trasf-prev-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Data della lettera</Label>
                                        <Input type="date" value={form.data_lettera} onChange={set("data_lettera")} className="mt-1 h-9" data-testid="trasf-letter-date-input" />
                                        <p className="mt-0.5 text-[10px] text-slate-400">Proposta: giorno lavorativo precedente la decorrenza</p>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">Note / precisazioni <span className="text-slate-400">(facoltative)</span></Label>
                                    <Textarea value={form.note} onChange={set("note")} rows={2} className="mt-1 text-sm" placeholder="Es. La trasformazione avviene a seguito dell'accordo raggiunto in data…" data-testid="trasf-note-input" />
                                </div>
                            </>
                        )}
                        <OrarioSection info={ctx.orario} orario={orario} setOrario={setOrario} totale={totaleOrario} />
                        {orarioIncompleto && (
                            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="letter-orario-warning">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> Variazione orario: indica le nuove ore settimanali e compila almeno un giorno della tabella.
                            </div>
                        )}
                        {warnings.map((w, i) => (
                            <div key={w} className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`proroga-warning-${i}`}>
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {w}
                            </div>
                        ))}
                        <p className="text-[11px] text-slate-500">
                            La lettera (carta intestata, firma dell'amministratore inserita automaticamente) viene archiviata nel fascicolo (categoria Comunicazione) e consegnata sul portale con richiesta di firma per accettazione; il dipendente riceve l'email di notifica. Alla conferma, il PDF viene completato con i riferimenti della firma digitale (data, ora, IP, impronta SHA-256).
                        </p>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                    <Button variant="outline" onClick={() => generate(false)} disabled={!ctx || invalid || !!busy || !ctx?.template_available} className="gap-1.5" data-testid="proroga-preview-btn">
                        {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Anteprima PDF
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="proroga-cancel-btn">Chiudi</Button>
                        <Button onClick={() => generate(true)} disabled={!ctx || invalid || !!busy || !ctx?.template_available} className="gap-1.5 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="proroga-send-btn">
                            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Genera e consegna
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
